import { action, flow, IObservableArray, makeAutoObservable, observable, reaction, when } from 'mobx';
import { BaseConnector } from './Base';
import EVMBase from './Wallets/EVMBase';
import type ExplorerRootStore from '../ExplorerRootStore';
import { Provider } from '../types';
import EIPWallet from './Wallets/EIP6963Wallet';
import WalletConnectEVM from './WalletConnect/EVM';
import { JsonRpcProvider } from 'ethers';

interface EIP6963ProviderInfo {
	uuid: string;
	name: string;
	icon: string;
	rdns: string;
}

interface EIP6963ProviderDetail {
	info: EIP6963ProviderInfo;
	provider: Provider;
}

export const ORDERED_EVM = ['Injected', 'Metamask', 'Brave', 'Coinbase', 'WalletConnect'];

function sortBy<T>(arr: T[], fn: (item: T) => number): T[] {
	return [...arr].sort((a, b) => fn(a) - fn(b));
}

export default class EVMConnector implements BaseConnector<EVMBase> {
	root: ExplorerRootStore;
	wallets: IObservableArray<EVMBase> = observable([]);
	lastUsed: EVMBase | undefined = undefined;
	walletConnect: WalletConnectEVM;
	staticProviders = observable.map<number, JsonRpcProvider>();
	constructor(root: ExplorerRootStore) {
		this.root = root;
		this.walletConnect = new WalletConnectEVM(this.root);
		makeAutoObservable<EVMConnector, 'root'>(this, { root: false, lastUsed: observable.ref }, { autoBind: true });
		this.initializeWallets();
		if (typeof window !== 'undefined') {
			window.addEventListener('eip6963:announceProvider', this.handleAnnounce as EventListener);
			window.dispatchEvent(new Event('eip6963:requestProvider'));
		}

		reaction(() => this.root.configs.evmChains, this.#resetStaticProviders);
		when(() => true, this.#resetStaticProviders);
	}

	initializeWallets = action(() => {
		this.wallets.push(this.walletConnect);
	});

	connect = flow(function* (this: EVMConnector, w: EVMBase, quick?: boolean) {
		yield w.connect(quick);
		this.lastUsed = w;
	});
	disconnect(w: EVMBase) {
		return w.disconnect();
	}

	get orderedWallets() {
		return sortBy(this.wallets, (w) => 1 + ORDERED_EVM.indexOf(w.name));
	}

	private handleAnnounce = action((e: CustomEventInit<EIP6963ProviderDetail>) => {
		if (!e.detail) return;
		const id = e.detail.info.rdns;
		const currentWallet = this.wallets.find((w) => 'id' in w && w.id === id);
		if (!currentWallet) {
			this.wallets.push(new EIPWallet(this.root, e.detail));
		}
	});

	#resetStaticProviders = action(() => {
		for (const chain of this.root.configs.evmChains) {
			try {
				this.staticProviders.get(chain.chainId)?.removeAllListeners?.();
			} catch {
				// ignore
			}
			this.staticProviders.set(
				chain.chainId,
				new JsonRpcProvider(chain.externalRpcURL || chain.rpcURL),
			);
		}
	});
}
